import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle, ClipboardCheck, Package, CalendarCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/shop`;
const fmtDay = (d) => new Date(`${d}T12:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'numeric', day: 'numeric' });

// End-of-week wrap-up for the shop foreman: every daily sheet and every project's consumables still waiting on approval.
export function WeekWrapup({ date, onChanged }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    try { setData((await axios.get(`${API}/foreman/week-wrapup`, { params: { date } })).data); } catch { /* ignore */ }
  }, [date]);
  useEffect(() => { load(); }, [load]);

  if (!data) return null;
  const dow = new Date().getDay();
  const weekend = dow === 5 || dow === 6;
  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); await load(); onChanged?.(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setBusy(false);
  };
  const approveDays = (days) => run(
    () => axios.post(`${API}/foreman/week-wrapup/approve-time`, { days: days.map(d => ({ employee_id: d.employee_id, date: d.date })) }),
    `${days.length} daily sheet${days.length === 1 ? '' : 's'} approved`);
  const approveCons = (wo) => run(
    () => axios.post(`${API}/foreman/week-wrapup/approve-consumables`, { date_from: data.date_from, date_to: data.date_to, ...(wo ? { work_order: wo } : {}) }),
    wo ? `Consumables approved on ${wo}` : 'All consumables approved for the week');
  const consCount = data.pending_consumables.reduce((s, w) => s + w.count, 0);
  const weekLabel = `${fmtDay(data.date_from)} – ${fmtDay(data.date_to)}`;

  return (
    <div className={`rounded-xl border shadow-sm p-4 ${data.clear ? 'bg-emerald-50 border-emerald-200' : weekend ? 'bg-amber-50 border-amber-300' : 'bg-white/70 border-slate-200'}`} data-testid="week-wrapup">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <CalendarCheck className={`w-5 h-5 ${data.clear ? 'text-emerald-600' : 'text-amber-600'}`} />
        <div className="flex-1 min-w-[200px]">
          <p className="text-xs text-slate-900 font-bold uppercase tracking-wider">Week wrap-up · {weekLabel}</p>
          <p className="text-[11px] text-slate-600">{data.clear ? 'Every daily sheet and every consumable is approved — nothing left for payroll.' : 'Approve the daily sheets and each project\'s consumables before the week closes (Friday / Saturday).'}</p>
        </div>
        {!data.clear && (
          <div className="flex items-center gap-2">
            {data.pending_days.length > 0 && (
              <button disabled={busy} onClick={() => approveDays(data.pending_days)} data-testid="wrapup-approve-all-days"
                className="px-3 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-semibold hover:bg-slate-700 disabled:opacity-50 inline-flex items-center gap-1">
                <ClipboardCheck className="w-3.5 h-3.5" /> Approve {data.pending_days.length} daily sheet{data.pending_days.length === 1 ? '' : 's'}
              </button>
            )}
            {consCount > 0 && (
              <button disabled={busy} onClick={() => approveCons(null)} data-testid="wrapup-approve-all-cons"
                className="px-3 py-1.5 rounded-lg bg-emerald-700 text-white text-xs font-semibold hover:bg-emerald-600 disabled:opacity-50 inline-flex items-center gap-1">
                <Package className="w-3.5 h-3.5" /> Approve {consCount} consumable line{consCount === 1 ? '' : 's'}
              </button>
            )}
          </div>
        )}
      </div>
      {data.clear ? (
        <p className="text-xs text-emerald-800 inline-flex items-center gap-1"><CheckCircle className="w-4 h-4" /> All clear</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="bg-white rounded-lg border border-slate-200 p-3">
            <p className="text-[11px] font-bold text-slate-800 uppercase mb-2">Daily sheets not approved ({data.pending_days.length})</p>
            {data.pending_days.length === 0 ? <p className="text-xs text-emerald-700">All approved</p> : (
              <ul className="space-y-1 max-h-56 overflow-y-auto">
                {data.pending_days.map(d => (
                  <li key={`${d.employee_id}-${d.date}`} className="flex items-center gap-2 text-xs" data-testid={`wrapup-day-${d.employee_id}-${d.date}`}>
                    <span className="w-20 text-slate-600">{fmtDay(d.date)}</span>
                    <span className="flex-1 font-semibold text-slate-900">{d.employee_name}</span>
                    <span className="text-slate-600 tabular-nums">{d.hours ? `${d.hours}h` : ''}</span>
                    <button disabled={busy} onClick={() => approveDays([d])} data-testid={`wrapup-approve-day-${d.employee_id}-${d.date}`} className="text-[10px] font-bold text-emerald-700 hover:underline">Approve</button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="bg-white rounded-lg border border-slate-200 p-3">
            <p className="text-[11px] font-bold text-slate-800 uppercase mb-2">Consumables pending by project ({data.pending_consumables.length} WO{data.pending_consumables.length === 1 ? '' : 's'})</p>
            {data.pending_consumables.length === 0 ? <p className="text-xs text-emerald-700">All approved</p> : (
              <ul className="space-y-1.5">
                {data.pending_consumables.map(w => (
                  <li key={w.work_order_number} className="text-xs" data-testid={`wrapup-wo-${w.work_order_number}`}>
                    <div className="flex items-center gap-2">
                      <button onClick={() => setOpen(open === w.work_order_number ? null : w.work_order_number)} className="font-mono font-bold text-blue-700 hover:underline">{w.work_order_number || '(no WO)'}</button>
                      <span className="flex-1 text-slate-700 truncate">{w.customer}</span>
                      <span className="text-slate-600">{w.count} line{w.count === 1 ? '' : 's'}{w.steel ? ` · ${w.steel} steel` : ''} · ${w.total.toFixed(2)}</span>
                      <button disabled={busy} onClick={() => approveCons(w.work_order_number)} className="text-[10px] font-bold text-emerald-700 hover:underline" data-testid={`wrapup-approve-wo-${w.work_order_number}`}>Approve</button>
                    </div>
                    {open === w.work_order_number && (
                      <ul className="mt-1 ml-3 space-y-0.5 text-[11px] text-slate-700">
                        {w.items.map(it => <li key={it.id}>{fmtDay(it.date)} · {it.employee_name} — {it.quantity} {it.unit} {it.item_name}{it.total_cost ? ` · $${Number(it.total_cost).toFixed(2)}` : ''}</li>)}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default WeekWrapup;
